import React, { lazy, useState, Suspense, useEffect } from 'react';
import { MdCalendarToday } from 'react-icons/md';
import { ReactDatePicker } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ArrowTriangleLeftIcon from '../Icons/ArrowTriangleLeftIcon';
import ArrowTriangleRightIcon from '../Icons/ArrowTriangleRightIcon';
import Button from '../Button';
import { dateFormatterInquiry } from '../../../helpers/utils';
import { montserrat } from '../../../helpers/utils/fonts';

interface DatePickerProps {
  title: string;
  onChange?: (date: Date | null) => void;
  onSubmit?: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  selected?: Date;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  dateFormat?: string;
  showTimeSelect?: boolean;
  filterDate?: (date: Date) => boolean;
}

const DatePickerLazy = lazy(() => import('react-datepicker'));

export default function DatePicker({
  title,
  onSubmit = () => {},
  minDate,
  maxDate,
  selected,
  error,
  required,
  disabled = false,
  placeholder = 'DD/MM/YYYY --:--',
  dateFormat = 'dd/MM/yyyy HH:mm',
  showTimeSelect = true,
  filterDate,
}: DatePickerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [displayedDate, setDisplayedDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!selected) {
      setSelectedDate(null);
    }
  }, [selected]);

  const CustomInput = React.forwardRef((props: any, ref: any) => {
    return (
      <div
        onClick={props.onClick}
        className={`flex border-b border-[#5f5f5f] bg-[#f8f8f8] mt-1 px-4 py-2 lg:px-5 lg:py-4  items-center h-[52.4px] ${montserrat.className}`}
      >
        <label
          ref={ref}
          className={`${props?.value ? 'text-reliableBlack' : 'text-reliableBlack60'} cursor-pointer`}
        >
          {props?.value &&
            displayedDate &&
            dateFormatterInquiry(displayedDate, false, null, '/', true)}
          {!props?.value && props?.placeholder}
        </label>
        <MdCalendarToday
          style={{
            width: '14px',
            height: '16px',
            position: 'absolute',
            top: '22px',
            right: '16px',
            color: disabled ? 'gray' : 'red',
          }}
        />
      </div>
    );
  });

  // Add a displayName property to CustomInput
  CustomInput.displayName = 'CustomInput';

  const calRef = React.useRef<ReactDatePicker | null>(null);

  return (
    <div className="w-[100%] customDatePickerWidth">
      <label className="font-[500] mb-3 text-[14px] leading-[17px] text-reliableBlack70">
        {title} {required && <span className="text-red-600">*</span>}
      </label>
      <Suspense>
        <DatePickerLazy
          filterDate={filterDate}
          ref={calRef}
          id="input-custom"
          className="w-full my-3 border-b-2 border-gray100 placeholder-gray80 text-[16px] py-[12px] !px-3 mt-[8px] text-reliableBlack90 font-[400]"
          showIcon
          popperClassName="w-full md:w-[633px]"
          calendarClassName="!w-full"
          dayClassName={() => '!w-8 !h-8 flex items-center justify-center mx-auto font-semibold'}
          placeholderText={placeholder}
          onChange={(e) => {
            setSelectedDate(e);
          }}
          selected={selected}
          showTimeSelect={showTimeSelect}
          customInput={<CustomInput />}
          timeFormat="HH:mm"
          dateFormat={dateFormat}
          shouldCloseOnSelect={false}
          customStyles={{
            dateIcon: {
              width: '40px',
              height: '40px',
            },
          }}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => {
            const months = [
              'January',
              'February',
              'March',
              'April',
              'May',
              'June',
              'July',
              'August',
              'September',
              'October',
              'November',
              'December',
            ];
            const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
            return (
              <div className="flex flex-col !w-full px-4">
                <div className="flex justify-between items-center py-2">
                  <button
                    onClick={(e) => {
                      e?.preventDefault();
                      decreaseMonth();
                    }}
                  >
                    <ArrowTriangleLeftIcon fill="#CE181E" />
                  </button>
                  <p className="text-center font-bold text-lg">
                    {months[date?.getMonth()]} {date?.getFullYear()?.toString()}
                  </p>
                  <button
                    onClick={(e) => {
                      e?.preventDefault();
                      increaseMonth();
                    }}
                  >
                    <ArrowTriangleRightIcon fill="#CE181E" />
                  </button>
                </div>
                <div className="flex justify-between items-center py-2 font-semibold">
                  {days.map((item, index) => (
                    <p key={index}>{item}</p>
                  ))}
                </div>
              </div>
            );
          }}
        >
          <div className="px-4 py-4">
            <Button
              className="w-full"
              textClassName="text-white"
              onClick={() => {
                if (selectedDate) {
                  onSubmit(selectedDate);
                  setDisplayedDate(selectedDate);
                } else {
                  onSubmit(minDate);
                  setDisplayedDate(minDate);
                }
                if (calRef?.current) calRef.current?.setOpen(false);
              }}
            >
              SUBMIT
            </Button>
          </div>
        </DatePickerLazy>
      </Suspense>

      {error && !selected && (
        <div className="flex items-center gap-[5.67px]">
          <span className="text-supportiveRed text-[14px] leading-[20px] font-[400]">{error}</span>
        </div>
      )}
    </div>
  );
}
